// First, create a new file: CRadioGroup.js
// Path: ../../../basecomp/newComponents/CRadioGroup.js

import React from "react";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { styled } from "@mui/material/styles";

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: "100%",
  "& .MuiFormLabel-root": {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(1),
  },
  "& .MuiFormControlLabel-root": {
    marginBottom: theme.spacing(1),
  },
  "& .MuiRadio-root": {
    color: theme.palette.primary.main,
    "&.Mui-checked": {
      color: theme.palette.primary.main,
    },
  },
}));

/**
 * Radio Group Component
 * @param {Object} props - Component props
 * @param {string} props.label - Label for the radio group
 * @param {Array} props.options - Array of option objects with id, value, and label
 * @param {string} props.value - Currently selected value
 * @param {Function} props.onChange - Function called when selection changes
 * @param {boolean} props.disabled - Whether the radio group is disabled
 * @param {Object} props.sx - Additional styles for the component
 * @param {string} props.name - Name attribute for the radio group
 * @param {string} props.labelPlacement - Placement of the label (start, end, top, bottom)
 * @returns {React.ReactElement} CRadioGroup component
 */
const CRadioGroup = ({
  label,
  options = [],
  value,
  onChange,
  disabled = false,
  sx = {},
  name = "radio-group",
  labelPlacement = "end",
  ...props
}) => {
  // Handle change function
  const handleChange = (event) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  // Remove duplicate labels before rendering
  const uniqueOptions = Array.from(
    new Map(options.map((item) => [item.label, item])).values()
  );


  return (
    <StyledFormControl component="fieldset" sx={sx} {...props}>
      {label && <FormLabel component="legend">{label}</FormLabel>}
      <RadioGroup
        aria-label={name}
        name={name}
        value={value}
        onChange={(e) => handleChange(e)}
      >
        {uniqueOptions.map((option) => (
          <FormControlLabel
            key={option.id}
            value={option.value}
            control={<Radio disabled={disabled} />}
            label={option.label}
            labelPlacement={labelPlacement}
            disabled={disabled || option.disabled}
          />
        ))}
      </RadioGroup>
    </StyledFormControl>
  );
};

export default CRadioGroup;